/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { Button, FlexColumn, FlexRow, Modal, Typography } from 'design-system';
import { CloseIcon, DownloadIcon } from 'design-system/components/Icons';
import VideoPlaybackModal from './VideoPlaybackModal';
import { buildBrandThemeStyle } from '../helpers/brandThemeStyle';
import { downloadMaterialFile, scheduleObjectUrlRevoke } from '../helpers/invitation-preview-helpers';

// function previewTitle(intl, kind) {
//   if (kind === 'video') {
//     return intl.formatMessage({ id: 'video-playback' });
//   }
//   return intl.formatMessage({ id: 'preview' });
// }

export default function PreviewMaterialModal({ open, onClose, material, accentColor }) {
  const intl = useIntl();
  const themeStyle = buildBrandThemeStyle(accentColor);
  const isDownloadingRef = useRef(false);
  const downloadBlobUrlRef = useRef(null);

  const kind = material?.kind;
  const fileUrl = material?.fileUrl;
  const name = material?.name;
  const downloadName = material?.downloadName;
  const extension = material?.extension;
  const docFileName = material
    ? [downloadName || name, extension?.replace(/^\./, '')].filter(Boolean).join('.')
    : '';

  const usesDocViewer = (kind === 'pdf' || kind === 'document') && fileUrl;

  useEffect(() => {
    if (!open || !fileUrl) {
      return undefined;
    }

    const isExternalBlob = fileUrl.startsWith('blob:') || fileUrl.startsWith('data:');
    if (isExternalBlob) {
      downloadBlobUrlRef.current = fileUrl;
      return () => {
        downloadBlobUrlRef.current = null;
      };
    }

    let cancelled = false;
    let ownedBlobUrl = null;

    const cacheDownloadBlob = async () => {
      try {
        const response = await fetch(fileUrl, { method: 'GET' });
        if (!response.ok) {
          return;
        }
        const blob = await response.blob();
        if (cancelled) {
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        ownedBlobUrl = objectUrl;
        downloadBlobUrlRef.current = objectUrl;
      } catch {
        // Download falls back to fetching on click.
      }
    };

    cacheDownloadBlob();

    return () => {
      cancelled = true;
      if (ownedBlobUrl) {
        scheduleObjectUrlRevoke(ownedBlobUrl);
        ownedBlobUrl = null;
      }
      downloadBlobUrlRef.current = null;
    };
  }, [fileUrl, open]);

  const docViewerDocuments = useMemo(
    () => (fileUrl ? [{ uri: fileUrl, fileName: docFileName || name || 'document' }] : []),
    [fileUrl, docFileName, name],
  );

  const docViewerConfig = useMemo(
    () => ({
      header: {
        disableHeader: true,
      },
    }),
    [],
  );

  const handleDownload = useCallback(async () => {
    const downloadUrl = downloadBlobUrlRef.current || fileUrl;
    if (!downloadUrl || isDownloadingRef.current) {
      return;
    }

    isDownloadingRef.current = true;
    try {
      await downloadMaterialFile(downloadUrl, docFileName || downloadName || name || 'download');
    } catch {
      toast.error(intl.formatMessage({ id: 'failed-to-load-the-file' }));
    } finally {
      isDownloadingRef.current = false;
    }
  }, [docFileName, downloadName, fileUrl, intl, name]);

  if (!material) {
    return null;
  }

  if (kind === 'video' && fileUrl) {
    return <VideoPlaybackModal isOpen={open} video={fileUrl} onClose={onClose} />;
  }

  return (
    <Modal
      open={open}
      variant="legacy"
      type="success"
      showIcon={false}
      showClose={false}
      onDismiss={onClose}
      className="cc-preview-material-modal"
    >
      <div className="cc-preview-material-modal__themed" style={themeStyle}>
      <FlexColumn className="document-modal-sepration">
        <FlexRow align="center" justify="end" fullWidth className="cc-preview-material-modal__toolbar">
          {fileUrl && kind !== 'link' ? (
            <Button
              type="button"
              variant="white"
              size="sm"
              className="cc-preview-material-modal__download-btn"
              aria-label={intl.formatMessage({ id: 'download' })}
              icon={
                <DownloadIcon
                  size={18}
                  fill={accentColor || 'currentColor'}
                  aria-hidden
                  className="cc-preview-material-modal__download-icon"
                />
              }
              onClick={handleDownload}
            />
          ) : null}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="cc-preview-material-modal__close-btn"
            aria-label={intl.formatMessage({ id: 'close' })}
            icon={
              <CloseIcon size={20} strokeWidth={1.5} aria-hidden className="cc-preview-material-modal__close-icon" />
            }
            onClick={onClose}
          />
        </FlexRow>
      </FlexColumn>
      <Modal.Body className="cc-preview-material-modal__body">
        <FlexColumn align="stretch" fullWidth>
          {kind === 'image' && fileUrl ? (
            <Typography component="img" src={fileUrl} alt={name || ''} className="cc-preview-material-modal__pdf" />
          ) : null}
          {usesDocViewer ? (
            <div className="cc-preview-material-modal__doc-viewer">
              <DocViewer
                prefetchMethod="GET"
                pluginRenderers={DocViewerRenderers}
                style={{ height: '100%', width: '100%' }}
                config={docViewerConfig}
                documents={docViewerDocuments}
              />
            </div>
          ) : null}
        </FlexColumn>
      </Modal.Body>
      </div>
    </Modal>
  );
}

PreviewMaterialModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accentColor: PropTypes.string,
  material: PropTypes.shape({
    kind: PropTypes.oneOf(['pdf', 'image', 'video', 'link', 'document']).isRequired,
    name: PropTypes.string,
    fileUrl: PropTypes.string,
    downloadName: PropTypes.string,
    extension: PropTypes.string,
  }),
};
